import type { DataOption } from '@/entities/column'
import type { InterpretationModel } from '@/entities/optimization-statistic'

import { useCallback, useMemo } from 'react'
import Markdown from 'markdown-to-jsx'
import { useTranslation } from 'react-i18next'

import { useColumnsSchemaQuery } from '@/entities/column'
import { getAnchoringTypeChoices } from '@/entities/pricing-campaign'

import { HiddenOnContext } from '@/shared/generated'

import { useFieldTranslationsResolver } from '../lib/useFieldTranslationsResolver'
import DataGrid from './interpretation-widgets/DataGrid'
import Elasticity from './interpretation-widgets/Elasticity'
import Lifecycle from './interpretation-widgets/Lifecycle'
import Limitations from './interpretation-widgets/Limitations'
import NotSupported from './interpretation-widgets/NotSupported'
import StopListRules from './interpretation-widgets/StopListRules'

export const InterpretationContent = ({
	data,
	children,
	params,
}: InterpretationModel & {
	params?: { optimization_group_id: string }
}) => {
	const { t } = useTranslation()

	const fields = useColumnsSchemaQuery<DataOption[]>(
		HiddenOnContext.Assortment,
		{
			select: (data) =>
				data.map(({ name, id }) => ({
					label: t(name, { ns: 'fields', defaultValue: name }),
					value: id,
				})),
		}
	)
	const resolveFieldTranslations = useFieldTranslationsResolver(data!)
	const fieldTranslations = resolveFieldTranslations(children!)
	const options = useMemo(
		() => ({
			anchor_type: getAnchoringTypeChoices(t),
			column: fields.data || [],
		}),
		[fields.data, t]
	)
	const withOptions = useCallback(
		(props?: Record<string, any>) => ({
			...(props || {}),
			options,
		}),
		[options]
	)
	const withParams = useCallback(
		(props?: Record<string, any>) => ({
			...(props || {}),
			params,
		}),
		[params]
	)

	return (
		<div className='markdown-txt prose prose-sm dark:prose-invert'>
			{fieldTranslations &&
				fieldTranslations.map((field: string) => (
					<Markdown
						key={field}
						options={{
							overrides: {
								DataGrid: {
									component: DataGrid,
									props: withOptions(data?.props?.DataGrid),
								},
								Limitations: {
									component: Limitations,
									props: withOptions(data?.props?.Limitations),
								},
								Elasticity: {
									component: Elasticity,
									props: {
										data: data?.props?.Elasticity || {},
									},
								},
								// This widgets are deprecated and will be removed in the future
								Contribution: {
									component: NotSupported,
									props: {
										data: data?.props?.Contribution || {},
									},
								},
								GrowthDecomposition: {
									component: NotSupported,
									props: {
										data: data?.props?.GrowthDecomposition || {},
									},
								},
								StopListRules: {
									component: StopListRules,
									props: withParams({
										rules: data?.props?.StopListRules || [],
									}),
								},
								Lifecycle: {
									component: Lifecycle,
									props: {
										data: data?.props?.Lifecycle || [],
									},
								},
							},
						}}
					>
						{`${field} \n`}
					</Markdown>
				))}
		</div>
	)
}
